import { siedCellVariant } from '../funerals/siedCell';

/**
 * SIED page list logic (T-148), kept apart from SiedPage so it's testable
 * without MUI. Rows staff removed as "Not ours" (dismissed_at set) never count
 * toward All / Not linked / Our turn — only the Removed filter shows them.
 */
export type RecordFilter = 'all' | 'unlinked' | 'turn' | 'removed';

export interface SiedListRow {
  linked_case?: { id: number; name: string } | null;
  sied_status?: string | null;
  detected_at: string;
  dismissed_at?: string | null;
}

export const DEFAULT_DISMISS_REASON = 'Not our case — shared by mistake';

const isRemoved = (r: SiedListRow) => !!r.dismissed_at;

export function siedListCounts(
  records: SiedListRow[]
): Record<RecordFilter, number> {
  const active = records.filter((r) => !isRemoved(r));
  return {
    all: active.length,
    unlinked: active.filter((r) => !r.linked_case).length,
    turn: active.filter((r) => siedCellVariant(r.sied_status) === 'turn')
      .length,
    removed: records.length - active.length,
  };
}

/** Rows for a filter: not-linked first (they need a person), then newest. */
export function siedListShown<T extends SiedListRow>(
  records: T[],
  filter: RecordFilter
): T[] {
  const list = records.filter((r) => {
    if (filter === 'removed') return isRemoved(r);
    if (isRemoved(r)) return false;
    if (filter === 'unlinked') return !r.linked_case;
    if (filter === 'turn') return siedCellVariant(r.sied_status) === 'turn';
    return true;
  });
  return [...list].sort(
    (a, b) =>
      Number(!!a.linked_case) - Number(!!b.linked_case) ||
      new Date(b.detected_at).getTime() - new Date(a.detected_at).getTime()
  );
}
